import { globalStores } from '@/editor/stores';
import { EContextAction, EEntityType, ESceneMode, IContextMenuItem, IEntityInfo } from '@/editor/types';
import { EShortcutId } from '@/editor/controller/keyboardController/config';
import { LAYOUT_ALLOWED_REGION_TYPE } from '@/utils/constant';
import {
  isMultiSelectDoorWindowOnly,
  isMultiSelectFurnitureAssemblyOrSepWallOnly,
  isMultiSelectFurnitureOrAssemblyOnly
} from '@/editor/utils/select/multi-select';
import { FLAGS } from '@/flags';
import { tkey } from '@/utils/development/i18n-ally';
type TContextMenuPolicy = Readonly<
  Record<
    EContextAction,
    Readonly<{
      availability: Readonly<Record<ESceneMode, ReadonlyArray<EEntityType>>>;
      label: Readonly<Partial<Record<EEntityType, string>> & { default: string }>;
      icon: Readonly<Partial<Record<EEntityType, string>> & { default: string }>;
      shortcut: EShortcutId | undefined;
      hidden?: boolean;
    }>
  >
>;

const MENU_POLICY: TContextMenuPolicy = {
  [EContextAction.ASSEMBLE]: {
    availability: {
      [ESceneMode.scene2d]: [EEntityType.MultiModel],
      [ESceneMode.scene3d]: []
    },
    label: {
      default: tkey('draw.house.tool.group')
    },
    icon: {
      default: 'icon-icon_feature_group_24'
    },
    shortcut: EShortcutId.ASSEMBLE
  },
  [EContextAction.UNASSEMBLE]: {
    availability: {
      [ESceneMode.scene2d]: [EEntityType.Assembly],
      [ESceneMode.scene3d]: []
    },
    label: {
      default: tkey('draw.house.tool.ungroup')
    },
    icon: {
      default: 'icon-icon_feature_ungroup_24'
    },
    shortcut: EShortcutId.UNASSEMBLE
  },
  [EContextAction.LAYOUT_FULL]: {
    availability: {
      [ESceneMode.scene2d]: [EEntityType.Room],
      [ESceneMode.scene3d]: []
    },
    label: {
      default: tkey('draw.layout.label')
    },
    icon: {
      default: 'icon-icon_feature_layout_24'
    },
    shortcut: undefined
  },
  [EContextAction.LAYOUT_PARTIAL]: {
    availability: {
      [ESceneMode.scene2d]: [EEntityType.Region],
      [ESceneMode.scene3d]: []
    },
    label: {
      default: tkey('draw.layout.tool.partTitle')
    },
    icon: {
      default: 'icon-icon_feature_layoutEdit_24'
    },
    shortcut: undefined
  },
  [EContextAction.ALIGN]: {
    availability: {
      [ESceneMode.scene2d]: [EEntityType.MultiModel],
      [ESceneMode.scene3d]: []
    },
    label: {
      default: tkey('draw.house.tool.align')
    },
    icon: {
      default: 'icon-icon_feature_AlignLeft_24'
    },
    shortcut: undefined,
    hidden: !FLAGS.FEATURES.ENABLE_MODEL_ALIGN
  },
  [EContextAction.FLIP]: {
    availability: {
      [ESceneMode.scene2d]: [EEntityType.DoorWindow],
      [ESceneMode.scene3d]: [EEntityType.DoorWindow]
    },
    label: {
      default: tkey('draw.house.tool.turnover')
    },
    icon: {
      default: 'icon-icon_feature_mirror_24'
    },
    shortcut: FLAGS.FEATURES.ENABLE_SHORTCUT_V1 ? EShortcutId.FLIP_ENTITY : undefined
  },
  [EContextAction.REPLACE]: {
    availability: {
      [ESceneMode.scene2d]: [
        EEntityType.DoorWindow,
        EEntityType.Furniture,
        EEntityType.Region,
        EEntityType.Room,
        ...(FLAGS.FEATURES.ENABLE_MULTI_MODEL_REPLACE ? [EEntityType.MultiModel] : [])
      ],
      [ESceneMode.scene3d]: [
        EEntityType.DoorWindow,
        EEntityType.Furniture,
        EEntityType.Region,
        EEntityType.Room,
        EEntityType.Face,
        ...(FLAGS.FEATURES.ENABLE_MULTI_MODEL_REPLACE ? [EEntityType.MultiModel] : [])
      ]
    },
    label: {
      default: tkey('draw.house.tool.replaceFurniture')
    },
    icon: { default: 'icon-icon_feature_replace_24' },
    shortcut: FLAGS.FEATURES.ENABLE_SHORTCUT_V1 ? EShortcutId.REPLACE_ENTITY : undefined
  },
  [EContextAction.APPLY_MATERIAL]: {
    availability: {
      [ESceneMode.scene2d]: [],
      [ESceneMode.scene3d]: [EEntityType.Face]
    },
    label: {
      default: tkey('draw.house.tool.allHouse')
    },
    icon: { default: 'icon-icon_feature_allroom_24' },
    shortcut: undefined
  },
  [EContextAction.COPY]: {
    availability: {
      [ESceneMode.scene2d]: [
        EEntityType.DoorWindow,
        EEntityType.Furniture,
        EEntityType.Column,
        EEntityType.Assembly,
        EEntityType.MultiModel
      ],
      [ESceneMode.scene3d]: []
    },
    label: {
      default: tkey('draw.house.tool.copy')
    },
    icon: { default: 'icon-icon_feature_copy_24' },
    shortcut: EShortcutId.COPY_ENTITY
  },
  [EContextAction.SELECT_SAME_MODEL]: {
    availability: {
      [ESceneMode.scene2d]: [EEntityType.Furniture, EEntityType.DoorWindow],
      [ESceneMode.scene3d]: []
    },
    label: {
      default: tkey('draw.house.tool.same')
    },
    icon: { default: 'icon-icon_feature_same_24' },
    shortcut: undefined,
    hidden: !FLAGS.FEATURES.ENABLE_SELECT_SAME_MODELS
  },
  [EContextAction.ARRAY]: {
    availability: {
      [ESceneMode.scene2d]: [EEntityType.Furniture, EEntityType.DoorWindow, EEntityType.Assembly],
      [ESceneMode.scene3d]: []
    },
    label: {
      default: tkey('draw.house.tool.array')
    },
    icon: {
      default: 'icon-icon_feature_ArrayMatrix_24',
      [EEntityType.Furniture]: 'icon-icon_feature_ArrayMatrix_24',
      [EEntityType.DoorWindow]: 'icon-icon_feature_ArrayLine_241'
    },
    shortcut: undefined,
    hidden: !FLAGS.FEATURES.ENABLE_MODEL_ARRAY
  },
  [EContextAction.CLEAR]: {
    availability: {
      [ESceneMode.scene2d]: [EEntityType.Room, EEntityType.Region],
      [ESceneMode.scene3d]: [EEntityType.Room, EEntityType.Region]
    },
    label: {
      default: tkey('draw.clear.tooltip.room'),
      [EEntityType.Room]: tkey('draw.clear.tooltip.room'),
      [EEntityType.Region]: tkey('draw.clean.room')
    },
    icon: {
      default: 'icon-icon_feature_clean_24'
    },
    shortcut: undefined
  },
  [EContextAction.REMOVE]: {
    availability: {
      [ESceneMode.scene2d]: [
        EEntityType.Wall,
        EEntityType.DoorWindow,
        EEntityType.Furniture,
        EEntityType.Column,
        EEntityType.SeparateWall,
        EEntityType.Room,
        EEntityType.Assembly,
        EEntityType.MultiModel
      ],
      [ESceneMode.scene3d]: [
        EEntityType.DoorWindow,
        EEntityType.Furniture,
        EEntityType.MultiModel,
        EEntityType.Assembly
      ]
    },
    label: {
      default: tkey('draw.house.tool.delete')
    },
    icon: { default: 'icon-icon_feature_delete_24' },
    shortcut: EShortcutId.REMOVE_ENTITY
  },
  [EContextAction.HIDE]: {
    availability: {
      [ESceneMode.scene2d]: [
        EEntityType.Wall,
        EEntityType.SeparateWall,
        EEntityType.DoorWindow,
        EEntityType.Furniture,
        EEntityType.Column,
        EEntityType.MultiModel
      ],
      [ESceneMode.scene3d]: [
        EEntityType.Wall,
        EEntityType.SeparateWall,
        EEntityType.DoorWindow,
        EEntityType.Furniture,
        EEntityType.Column,
        EEntityType.MultiModel
      ]
    },
    label: {
      default: tkey('draw.house.tool.hide')
    },
    icon: { default: 'icon-icon_feature_hide_24' },
    shortcut: undefined,
    hidden: true
  },
  [EContextAction.LOCK]: {
    availability: {
      [ESceneMode.scene2d]: [
        EEntityType.Wall,
        EEntityType.SeparateWall,
        EEntityType.DoorWindow,
        EEntityType.Furniture,
        EEntityType.Column
      ],
      [ESceneMode.scene3d]: [
        EEntityType.Wall,
        EEntityType.SeparateWall,
        EEntityType.DoorWindow,
        EEntityType.Furniture,
        EEntityType.Column
      ]
    },
    label: {
      default: tkey('draw.house.tool.lock')
    },
    icon: { default: 'icon-icon_feature_lock_24' },
    shortcut: undefined,
    hidden: true
  }
};

interface IGetAvailableMenuItemParams {
  sceneMode: ESceneMode;
  entityInfo: IEntityInfo;
}

interface IGetMenuItemExecutionStateParams extends IGetAvailableMenuItemParams {
  actionType: EContextAction;
}

/** 获取可用的对象菜单数组 */
export function getAvailableMenuItems({ sceneMode, entityInfo }: IGetAvailableMenuItemParams) {
  let menuItems = (Object.entries(MENU_POLICY) as TObjectEntries<TContextMenuPolicy>)
    .map<IContextMenuItem | undefined>(([action, policy]) => {
      const { label, icon } = policy;
      if (policy.hidden !== true && policy.availability[sceneMode].includes(entityInfo.type)) {
        return {
          action,
          label: label[entityInfo.type] ?? label.default,
          icon: icon[entityInfo.type] ?? icon.default,
          shortcut: policy.shortcut
        };
      }
      return undefined;
    })
    .filter((item) => item !== undefined);

  if (entityInfo.type === EEntityType.MultiModel) {
    const multiSelectData = globalStores.getDataByIdType('', EEntityType.MultiModel);
    if (isMultiSelectFurnitureOrAssemblyOnly(multiSelectData)) {
      const modelIdSet = new Set<string>();
      let hasAssemblyFurniture = false;
      let hasMoreThanOneModel = false;
      for (const { id, type } of multiSelectData.modelInfos) {
        switch (type) {
          case EEntityType.Furniture: {
            const furnitureData = globalStores.modelStore.getFurnitureData(id);

            if (furnitureData) {
              modelIdSet.add(furnitureData.modelId);
            }

            if (!hasMoreThanOneModel && modelIdSet.size > 1) {
              hasMoreThanOneModel = true;
            }

            if (!hasAssemblyFurniture) {
              hasAssemblyFurniture = globalStores.assemblyStore.isAssemblyFuniture(id);
            }
            break;
          }
          case EEntityType.Assembly: {
            const assemblyData = globalStores.assemblyStore.getAssemblyData(id);
            if (!hasMoreThanOneModel && assemblyData) {
              hasMoreThanOneModel = true;
            }
            break;
          }
        }
        if (hasMoreThanOneModel && hasAssemblyFurniture) {
          break;
        }
      }

      if (hasMoreThanOneModel) {
        // 替换操作只在选中的所有家具都是同款时才显示
        menuItems = menuItems.filter((item) => item.action !== EContextAction.REPLACE);
      }

      if (hasAssemblyFurniture) {
        // 多选家具中存在已成组家具时禁用成组操作
        menuItems = menuItems.map((item) => {
          if (item.action === EContextAction.ASSEMBLE) {
            return { ...item, disabled: true };
          } else {
            return item;
          }
        });
      }
    } else if (isMultiSelectDoorWindowOnly(multiSelectData)) {
      let hasMoreMaterial = false;
      const doorWindowData = globalStores.modelStore.getWindowData(
        multiSelectData.modelInfos.map((item) => item.id)[0]
      );
      if (doorWindowData) {
        hasMoreMaterial = globalStores.modelStore.getModelsByType(doorWindowData.type).length > 1;
      }
      if (!hasMoreMaterial) {
        menuItems = menuItems.filter((item) => item.action !== EContextAction.REPLACE);
      }

      // 门窗隐藏复制、对齐和成组
      menuItems = menuItems.filter(
        (item) => ![EContextAction.COPY, EContextAction.ALIGN, EContextAction.ASSEMBLE].includes(item.action)
      );
    } else if (isMultiSelectFurnitureAssemblyOrSepWallOnly(multiSelectData)) {
      // 走到这里必然含隔墙（纯家具/组合已被上面的分支接走）：隔墙不支持成组/复制/替换/对齐，只留删除
      menuItems = menuItems.filter((item) => item.action === EContextAction.REMOVE);
    } else {
      // 隐藏复制/成组/替换/移除/对齐
      menuItems = menuItems.filter(
        (item) =>
          ![
            EContextAction.COPY,
            EContextAction.ASSEMBLE,
            EContextAction.REPLACE,
            EContextAction.REMOVE,
            EContextAction.ALIGN
          ].includes(item.action)
      );
    }
  }

  // 选择同款只有在场景内有同款才显示
  if ([EEntityType.Furniture, EEntityType.DoorWindow].includes(entityInfo.type)) {
    let hasMore = false;
    switch (entityInfo.type) {
      case EEntityType.Furniture: {
        hasMore = globalStores.modelStore.getSameModelFurnitures(entityInfo.entityId).length > 1;
        break;
      }
      case EEntityType.DoorWindow: {
        hasMore = globalStores.modelStore.getSameModelDoorWindows(entityInfo.entityId).length > 1;
        break;
      }
    }
    if (!hasMore) {
      menuItems = menuItems.filter((item) => item.action !== EContextAction.SELECT_SAME_MODEL);
    }
  }

  // 房间存在分区时禁用替换材质
  if (entityInfo.type === EEntityType.Room) {
    const roomRegion = globalStores.regionStore.getRoomRegions(entityInfo.entityId);
    if (roomRegion.length > 1) {
      menuItems = menuItems.map((item) => {
        if (item.action === EContextAction.REPLACE) {
          return { ...item, disabled: true };
        } else {
          return item;
        }
      });
    }
  }

  // 分区类型不符合时禁用局部布局
  if (entityInfo.type === EEntityType.Region) {
    const regionData = globalStores.regionStore.getRegionData(entityInfo.entityId);
    if (regionData) {
      const isLayoutAllowed = LAYOUT_ALLOWED_REGION_TYPE.includes(regionData.regionInfo.type);
      if (!isLayoutAllowed) {
        menuItems = menuItems.map((item) => {
          if (item.action === EContextAction.LAYOUT_PARTIAL) {
            return { ...item, disabled: true };
          } else {
            return item;
          }
        });
      }
    }
  }

  // 门窗没有可替换的模型时隐藏替换材质
  if (entityInfo.type === EEntityType.DoorWindow) {
    let hasMoreMaterial = false;
    let isArcWall = true;
    const doorWindowData = globalStores.modelStore.getWindowData(entityInfo.entityId);
    if (doorWindowData) {
      hasMoreMaterial = globalStores.modelStore.getModelsByType(doorWindowData.type).length > 1;
      const { wallType = EEntityType.Wall, relativeWallId } = doorWindowData.relativeWallInfo;
      if (wallType === EEntityType.SeparateWall) {
        const wallData = globalStores.separateWallStore.getSepData(relativeWallId, false);
        if (wallData && wallData.center === undefined) {
          isArcWall = false;
        }
      } else {
        const wall = globalStores.wallStore.getWallData(relativeWallId);
        if (wall && wall.baseData.center === undefined) {
          isArcWall = false;
        }
      }
    }
    if (!hasMoreMaterial) {
      menuItems = menuItems.filter((item) => item.action !== EContextAction.REPLACE);
    }

    // TODO: 弧形墙暂不支持阵列，后续需要支持
    if (isArcWall) {
      menuItems = menuItems.filter((item) => item.action !== EContextAction.ARRAY);
    }
  }

  return menuItems;
}

export function getMenuItemExecutionState({ actionType, ...params }: IGetMenuItemExecutionStateParams): {
  executable: boolean;
  menuItem?: IContextMenuItem;
} {
  const menuItem = getAvailableMenuItems(params).find((item) => item.action === actionType);

  const executable = menuItem !== undefined && !menuItem.disabled;

  return { executable, menuItem };
}
